import { supabase } from './supabase';

/**
 * Partner programme reads for ControlTower.
 *
 * Every money figure arrives already summed by `fn_partner_overview` /
 * `fn_partner_statement`. Nothing here adds, multiplies or applies a rate — per
 * the "dumb frontend" rule in CLAUDE.md all financial arithmetic stays in
 * Postgres, and commission is money owed to a real person.
 *
 * Both RPCs are SECURITY INVOKER, so the platform-admin RLS policies on
 * partners / partner_referrals / partner_commissions do the access control. A
 * non-admin calling these gets an empty result rather than an error.
 */

export interface PartnerOverviewRow {
    partner_id: string;
    code: string;
    display_name: string;
    status: 'active' | 'paused' | 'terminated';
    referred_orgs: number;
    converted_orgs: number;
    /** Orgs that generated commission in this period (i.e. actually paid us). */
    paying_orgs_period: number;
    period_start: string;
    /** This period's earnings, net of clawbacks recorded in it. Can be negative. */
    period_net_pence: number;
    lifetime_paid_pence: number;
    /** Accrued but not yet paid out, all time. This is what becomes a transfer. */
    outstanding_pence: number;
}

/** First day of the month, as the `date` the RPC expects. */
export function periodKey(d: Date = new Date()): string {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

export async function listPartnerOverview(period: string = periodKey()): Promise<PartnerOverviewRow[]> {
    const { data, error } = await supabase.rpc('fn_partner_overview', { p_period: period });
    if (error) throw error;
    return (data ?? []) as PartnerOverviewRow[];
}

/**
 * Pence to a displayable pound string. Negative is real and meaningful here —
 * a clawback can leave a partner owing us against the next statement — so it is
 * rendered as "-£12.00" rather than hidden or clamped to zero.
 */
export function formatPence(pence: number): string {
    const negative = pence < 0;
    const abs = Math.abs(pence);
    const formatted = `£${(abs / 100).toFixed(2)}`;
    return negative ? `-${formatted}` : formatted;
}

const CSV_COLUMNS: { key: keyof PartnerOverviewRow; label: string; money?: boolean }[] = [
    { key: 'code', label: 'Code' },
    { key: 'display_name', label: 'Partner' },
    { key: 'status', label: 'Status' },
    { key: 'referred_orgs', label: 'Referred' },
    { key: 'converted_orgs', label: 'Converted' },
    { key: 'paying_orgs_period', label: 'Paying this period' },
    { key: 'period_net_pence', label: 'Period net (GBP)', money: true },
    { key: 'outstanding_pence', label: 'Outstanding (GBP)', money: true },
    { key: 'lifetime_paid_pence', label: 'Paid to date (GBP)', money: true },
];

/**
 * CSV for pasting into a self-billing invoice.
 *
 * Money is emitted as a plain decimal with no currency symbol and no thousands
 * separator, because this ends up in a spreadsheet that has to add it up.
 * Fields are quoted and internal quotes doubled — a partner display name is
 * free text and a stray comma would otherwise shift every column after it.
 */
export function toCsv(rows: PartnerOverviewRow[]): string {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [CSV_COLUMNS.map(c => escape(c.label)).join(',')];
    for (const row of rows) {
        lines.push(CSV_COLUMNS.map(c => {
            const raw = row[c.key];
            return escape(c.money ? (Number(raw) / 100).toFixed(2) : String(raw ?? ''));
        }).join(','));
    }
    return lines.join('\r\n');
}

// ─── Referral link ──────────────────────────────────────────────────────────

/**
 * The marketing origin partners send their audience to.
 *
 * Deliberately the marketing site rather than the app: the partner's audience
 * has never heard of ForgeDash, so they need the pitch before the signup form.
 * `RefLinkBridge` on that site carries the `?ref=` across to the app.
 */
const MARKETING_ORIGIN =
    (import.meta.env.VITE_MARKETING_URL as string | undefined)?.replace(/\/$/, '') ||
    'https://forgedash.uk';

/** The exact string to paste into a partner's email. */
export function referralLink(code: string): string {
    return `${MARKETING_ORIGIN}/?ref=${encodeURIComponent(code)}`;
}

// ─── Admin CRUD (platform admins only; enforced by RLS) ─────────────────────

export interface PartnerRecord {
    id: string;
    code: string;
    display_name: string;
    contact_email: string | null;
    org_id: string | null;
    status: 'active' | 'paused' | 'terminated';
    rate_bps_initial: number;
    rate_bps_tail: number;
    initial_term_months: number;
    notes: string | null;
    joined_at: string;
}

const PARTNER_COLS =
    'id,code,display_name,contact_email,org_id,status,rate_bps_initial,rate_bps_tail,initial_term_months,notes,joined_at';

export interface PartnerInput {
    code: string;
    display_name: string;
    contact_email: string | null;
    rate_bps_initial: number;
    rate_bps_tail: number;
    initial_term_months: number;
}

export async function listPartners(): Promise<PartnerRecord[]> {
    const { data, error } = await supabase
        .from('partners')
        .select(PARTNER_COLS)
        .order('display_name');
    if (error) throw error;
    return (data as PartnerRecord[]) ?? [];
}

export interface ProvisionResult {
    partner: PartnerRecord;
    promotion_code: { id: string; code: string; coupon: string };
}

/**
 * Create a partner AND its Stripe promotion code together.
 *
 * Prefer this over `createPartner` for anything a human does. A partner code is
 * two objects that must agree — the Stripe promotion code the customer redeems
 * and the `partners` row commission accrues against — and creating one without
 * the other is the worst failure the programme has: with the Stripe code alone
 * the customer gets their discount and commission silently never accrues,
 * because `fn_provision_org` ignores an unknown code on purpose so a typo
 * cannot break a signup.
 *
 * The browser cannot talk to Stripe (the secret key never leaves the edge
 * runtime), so this goes through the `partner-provision` function, which also
 * owns the ordering and the rollback.
 */
export async function provisionPartner(input: PartnerInput): Promise<ProvisionResult> {
    const { data, error } = await supabase.functions.invoke('partner-provision', {
        body: { ...input, code: input.code.trim().toUpperCase() },
    });
    if (error) {
        // functions.invoke surfaces a generic FunctionsHttpError; the useful
        // message is in the response body, so dig it out rather than showing
        // the admin "Edge Function returned a non-2xx status code".
        let detail = '';
        try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
                detail = (await ctx.json())?.error ?? '';
            }
        } catch { /* fall through to the generic message */ }
        throw new Error(detail || error.message || 'Could not create the partner');
    }
    return data as ProvisionResult;
}

/**
 * Row-only creation, no Stripe side.
 *
 * Kept for the case where the promotion code already exists in Stripe — the
 * five hand-cut `PARTNER-COMP-*` codes, or a code created before
 * `partner-provision` existed. For a new partner use `provisionPartner`.
 */
export async function createPartner(input: PartnerInput): Promise<PartnerRecord> {
    // Upper-cased here as well as in the database CHECK. The constraint is the
    // real guarantee; this just means a lower-case code succeeds rather than
    // bouncing back as a constraint violation the admin has to interpret.
    const { data, error } = await supabase
        .from('partners')
        .insert({ ...input, code: input.code.trim().toUpperCase() })
        .select(PARTNER_COLS)
        .single();
    if (error) throw error;
    return data as PartnerRecord;
}

/** Rates and status only — `code` is never editable; see the note in the panel. */
export async function updatePartner(
    id: string,
    patch: Partial<Pick<PartnerRecord, 'display_name' | 'contact_email' | 'status' | 'rate_bps_initial' | 'rate_bps_tail' | 'initial_term_months' | 'notes'>>,
): Promise<void> {
    const { error } = await supabase.from('partners').update(patch).eq('id', id);
    if (error) throw error;
}

// ─── Drill-down ─────────────────────────────────────────────────────────────

export interface PartnerReferralRow {
    org_id: string;
    org_name: string;
    first_seen_at: string;
    converted_at: string | null;
    subscription_tier: string;
    source: 'ref_link' | 'promo_code';
    commission_count: number;
    lifetime_pence: number;
    clawed_back_pence: number;
}

export async function listPartnerReferrals(partnerId: string): Promise<PartnerReferralRow[]> {
    const { data, error } = await supabase.rpc('fn_partner_referral_detail', {
        p_partner_id: partnerId,
    });
    if (error) throw error;
    return (data as PartnerReferralRow[]) ?? [];
}

// ─── Payouts ────────────────────────────────────────────────────────────────

export interface MarkPaidResult {
    marked_count: number;
    marked_pence: number;
}

/**
 * Record that a bank transfer went out.
 *
 * `period` omitted settles everything currently due, which is what actually
 * happens — you pay a partner what you owe them, not one month of it. The RPC
 * returns what it really marked so the caller can check it against the amount
 * that was actually sent.
 */
export async function markPartnerPaid(partnerId: string, period?: string): Promise<MarkPaidResult> {
    const { data, error } = await supabase.rpc('fn_partner_mark_paid', {
        p_partner_id: partnerId,
        p_period: period ?? null,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return (row as MarkPaidResult) ?? { marked_count: 0, marked_pence: 0 };
}
